import React, { useCallback, useEffect, useRef, useState } from "react";

import { api } from "../lib/api";
import { colors } from "../theme/colors";
import { DemografiaCategorias, DemografiaItem, LinkedInHistory, LinkedInResponse } from "../types/crm";

const SOCIOS = [
  { key: "renan", nome: "Renan Nishimoto" },
  { key: "felipe", nome: "Felipe Kosloski" },
  { key: "guilherme", nome: "Guilherme Goya" },
];

const C = colors;

function num(v: number | null | undefined): string {
  return v === null || v === undefined ? "—" : v.toLocaleString("pt-BR");
}
function pct(v: number | null | undefined): string {
  return v === null || v === undefined ? "—" : `${Math.round(v * 100)}%`;
}
function ddmm(iso: string | null): string {
  if (!iso) return "—";
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}
function totalEngaj(d: LinkedInResponse | null | undefined): number {
  return (d?.posts ?? []).reduce((s, p) => s + (p.engajamentos ?? 0), 0);
}

// ---- Tiles e barras (tema escuro) ----

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div
      className="rounded-2xl p-4"
      style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }}
    >
      <p className="text-[11px] uppercase tracking-wide" style={{ color: C.lilacSoft }}>
        {label}
      </p>
      <p className="mt-1.5 text-2xl font-semibold text-white">{value}</p>
      {hint && <p className="mt-0.5 text-[11px]" style={{ color: "rgba(215,201,215,0.7)" }}>{hint}</p>}
    </div>
  );
}

function BarList({
  items,
  color = C.accent,
  max,
}: {
  items: { label: string; value: number | null; valueLabel: string }[];
  color?: string;
  max?: number;
}) {
  const m = max ?? Math.max(0.0001, ...items.map((i) => i.value ?? 0));
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i}>
          <div className="flex items-center justify-between text-xs" style={{ color: C.lilacSoft }}>
            <span className="truncate pr-2">{it.label}</span>
            <span className="font-semibold text-white">{it.valueLabel}</span>
          </div>
          <div className="mt-1 h-2 rounded-full" style={{ background: "rgba(255,255,255,0.08)" }}>
            <div
              className="h-2 rounded-full"
              style={{ width: `${Math.max(3, ((it.value ?? 0) / m) * 100)}%`, background: color }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function Insight({ children, tone = "mint" }: { children: React.ReactNode; tone?: "mint" | "plum" | "cream" }) {
  const bg =
    tone === "mint"
      ? "rgba(96,187,155,0.14)"
      : tone === "cream"
      ? "rgba(240,217,168,0.14)"
      : "rgba(183,159,183,0.14)";
  const dot = tone === "mint" ? C.accent : tone === "cream" ? C.cream : C.lilacSoft;
  return (
    <div className="flex gap-2 rounded-xl p-3 text-sm" style={{ background: bg, color: "#F4EFF4" }}>
      <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full" style={{ background: dot }} />
      <p className="leading-snug">{children}</p>
    </div>
  );
}

function cat(demo: DemografiaCategorias | undefined, name: string): DemografiaItem[] {
  return (demo?.[name] ?? []).filter((i) => i.valor);
}
function topItems(items: DemografiaItem[], n = 6) {
  return items.slice(0, n).map((i) => ({ label: i.valor, value: i.pct, valueLabel: i.pct_label }));
}

// ---- Insights automáticos ----

function publicacaoInsights(d: LinkedInResponse): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const eng = totalEngaj(d);
  const impr = d.resumo?.impressoes_total ?? 0;
  const n = d.posts.length;
  if (impr > 0) {
    const taxa = eng / impr;
    out.push(
      <>
        Taxa de engajamento de <strong>{(taxa * 100).toFixed(2)}%</strong> (engajamentos ÷ impressões).{" "}
        {taxa >= 0.03 ? "Acima da média típica do LinkedIn (2–3%)." : "Dentro/abaixo da média típica (2–3%)."}
      </>
    );
  }
  if (n > 0) {
    out.push(
      <>
        Média de <strong>{Math.round(eng / n).toLocaleString("pt-BR")}</strong> engajamentos por
        publicação, em {n} posts.
      </>
    );
    const best = [...d.posts].sort((a, b) => (b.engajamentos ?? 0) - (a.engajamentos ?? 0))[0];
    if (best?.engajamentos)
      out.push(
        <>
          Melhor publicação ({ddmm(best.data)}): <strong>{best.engajamentos.toLocaleString("pt-BR")}</strong>{" "}
          engajamentos — vale repetir o formato.
        </>
      );
  }
  const alc = d.resumo?.usuarios_alcancados ?? 0;
  const seg = d.resumo?.seguidores_total ?? 0;
  if (alc > 0 && seg > 0)
    out.push(
      <>
        Alcançou <strong>{alc.toLocaleString("pt-BR")}</strong> pessoas — cerca de{" "}
        <strong>{(alc / seg).toFixed(1)}×</strong> a base de seguidores, indicando alcance além da rede.
      </>
    );
  return out;
}

function conteudoInsights(demo: DemografiaCategorias | undefined): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const setor = cat(demo, "Setor");
  const nivel = cat(demo, "Nível de experiência");
  const loc = cat(demo, "Localidade");
  const tam = cat(demo, "Tamanho da empresa");
  const empresa = cat(demo, "Empresa");
  if (setor[0])
    out.push(
      <>
        Principal setor alcançado: <strong>{setor[0].valor}</strong> ({setor[0].pct_label})
        {setor[1] ? <>, seguido de <strong>{setor[1].valor}</strong> ({setor[1].pct_label}).</> : "."}
      </>
    );
  if (nivel[0])
    out.push(
      <>
        Perfil de senioridade: predomínio de <strong>{nivel[0].valor}</strong> ({nivel[0].pct_label})
        {nivel[1] ? <> e <strong>{nivel[1].valor}</strong> ({nivel[1].pct_label}).</> : "."}
      </>
    );
  if (loc[0] && (loc[0].pct ?? 0) >= 0.25)
    out.push(
      <>
        <strong>{loc[0].pct_label}</strong> do público está em <strong>{loc[0].valor}</strong> — forte
        concentração geográfica.
      </>
    );
  const grande = tam.find((t) => /10\.?001|acima|mais de 10/i.test(t.valor));
  if (grande)
    out.push(
      <>
        <strong>{grande.pct_label}</strong> vêm de grandes empresas (10k+ funcionários) — público
        corporativo/enterprise.
      </>
    );
  if (empresa.length)
    out.push(
      <>
        Entre as empresas que mais aparecem: {empresa.slice(0, 4).map((e) => e.valor).join(", ")}.
      </>
    );
  return out;
}

export function LinkedInPanel() {
  const [sub, setSub] = useState<"publicacao" | "conteudo" | "historico">("publicacao");
  const [view, setView] = useState<string>("guilherme");
  const [fonte, setFonte] = useState<"conteudo" | "publico">("conteudo");
  const [data, setData] = useState<LinkedInResponse | null>(null);
  const [all, setAll] = useState<Record<string, LinkedInResponse>>({});
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<{ tone: "ok" | "err"; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadSocio = useCallback((socio: string) => {
    setLoading(true);
    api.get<LinkedInResponse>(`/linkedin/?socio=${socio}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (view === "comparativo") {
      Promise.all(SOCIOS.map((s) => api.get<LinkedInResponse>(`/linkedin/?socio=${s.key}`))).then((res) => {
        const map: Record<string, LinkedInResponse> = {};
        res.forEach((r, i) => (map[SOCIOS[i].key] = r.data));
        setAll(map);
      });
    } else {
      loadSocio(view);
    }
  }, [view, loadSocio]);

  const handleUpload = async (file: File) => {
    setStatus(null);
    const form = new FormData();
    form.append("socio", view);
    form.append("kind", sub);
    form.append("file", file);
    try {
      const r = await api.post<LinkedInResponse>("/linkedin/import/", form);
      setData(r.data);
      setStatus({
        tone: "ok",
        text:
          sub === "publicacao"
            ? `${r.data.imported ?? 0} publicações importadas.`
            : `${r.data.imported_demografia ?? 0} linhas de demografia importadas.`,
      });
    } catch {
      setStatus({ tone: "err", text: "Não consegui ler a planilha. Use o export .xlsx do LinkedIn." });
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleClear = async () => {
    await api.post("/linkedin/clear/", { socio: view, kind: sub });
    loadSocio(view);
    setStatus(null);
  };

  const pillStyle = (active: boolean): React.CSSProperties =>
    active
      ? { background: C.accent, color: "#0E2A20" }
      : { background: "rgba(255,255,255,0.08)", color: C.lilacSoft };

  const uploadBox = (
    <div
      className="rounded-2xl border border-dashed p-4 text-center"
      style={{ borderColor: "rgba(215,201,215,0.3)", background: "rgba(255,255,255,0.03)" }}
    >
      <p className="text-sm font-medium text-white">
        {sub === "publicacao" ? "Subir export de Publicação (.xlsx)" : "Subir export de Conteúdo (.xlsx)"}
      </p>
      <p className="mt-0.5 text-[11px]" style={{ color: "rgba(215,201,215,0.7)" }}>
        Use o AggregateAnalytics do LinkedIn — leio as abas de{" "}
        {sub === "publicacao" ? "publicações, descoberta e seguidores" : "demografia (público e conteúdo)"}.
      </p>
      <div className="mt-3 flex items-center justify-center gap-2">
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx"
          onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
          className="hidden"
          id="ln-file"
          disabled={view === "comparativo"}
        />
        <label
          htmlFor="ln-file"
          className="cursor-pointer rounded-full px-4 py-2 text-sm font-semibold"
          style={{ background: C.accent, color: "#0E2A20", opacity: view === "comparativo" ? 0.4 : 1 }}
        >
          Selecionar planilha
        </label>
        <button
          type="button"
          onClick={handleClear}
          className="rounded-full px-4 py-2 text-sm font-medium"
          style={{ border: "1px solid rgba(215,201,215,0.3)", color: C.lilacSoft }}
        >
          Limpar
        </button>
      </div>
      {view === "comparativo" && (
        <p className="mt-2 text-[11px]" style={{ color: "rgba(215,201,215,0.7)" }}>
          Selecione um sócio para importar.
        </p>
      )}
      {status && (
        <p className="mt-2 text-xs" style={{ color: status.tone === "ok" ? C.accent : "#FCA5A5" }}>
          {status.text}
        </p>
      )}
    </div>
  );

  return (
    <div
      className="mt-5 rounded-3xl p-6"
      style={{
        background: `linear-gradient(145deg, ${C.plumDeep} 0%, ${C.plumNight} 55%, ${C.plum} 140%)`,
        boxShadow: "0 20px 60px rgba(30,20,30,0.35)",
      }}
    >
      {/* Cabeçalho */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em]" style={{ color: C.accent }}>
            LinkedIn · inteligência de audiência
          </p>
          <h2 className="mt-1 text-lg font-semibold text-white">
            {view === "comparativo" ? "Comparativo entre sócios" : SOCIOS.find((s) => s.key === view)?.nome}
          </h2>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {SOCIOS.map((s) => (
            <button key={s.key} type="button" onClick={() => setView(s.key)} className="rounded-full px-3 py-1 text-sm font-medium" style={pillStyle(view === s.key)}>
              {s.nome.split(" ")[0]}
            </button>
          ))}
          <button type="button" onClick={() => setView("comparativo")} className="rounded-full px-3 py-1 text-sm font-medium" style={pillStyle(view === "comparativo")}>
            Comparativo
          </button>
        </div>
      </div>

      {/* Sub-abas */}
      <div className="mt-4 inline-flex rounded-full p-1" style={{ background: "rgba(255,255,255,0.06)" }}>
        {([["publicacao", "Publicação"], ["conteudo", "Conteúdo"], ["historico", "Histórico"]] as ["publicacao" | "conteudo" | "historico", string][]).map(([k, label]) => (
          <button key={k} type="button" onClick={() => setSub(k)} className="rounded-full px-4 py-1.5 text-sm font-medium" style={pillStyle(sub === k)}>
            {label}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {sub === "historico" ? (
          view === "comparativo" ? (
            <p className="text-sm" style={{ color: C.lilacSoft }}>Selecione um sócio para ver o histórico de cargas.</p>
          ) : (
            <HistoryView socio={view} />
          )
        ) : view === "comparativo" ? (
          sub === "publicacao" ? <ComparativoPublicacao all={all} /> : <ComparativoConteudo all={all} />
        ) : loading ? (
          <p className="text-sm" style={{ color: C.lilacSoft }}>Carregando...</p>
        ) : sub === "publicacao" ? (
          <PublicacaoView data={data} uploadBox={uploadBox} />
        ) : (
          <ConteudoView data={data} fonte={fonte} setFonte={setFonte} uploadBox={uploadBox} />
        )}
      </div>
    </div>
  );
}

// ---- Publicação (um sócio) ----
function PublicacaoView({ data, uploadBox }: { data: LinkedInResponse | null; uploadBox: React.ReactNode }) {
  const eng = totalEngaj(data);
  const impr = data?.resumo?.impressoes_total ?? null;
  const taxa = impr && impr > 0 ? eng / impr : null;
  const top = [...(data?.posts ?? [])]
    .filter((p) => (p.engajamentos ?? 0) > 0)
    .sort((a, b) => (b.engajamentos ?? 0) - (a.engajamentos ?? 0))
    .slice(0, 8);
  const insights = data ? publicacaoInsights(data) : [];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-7">
        <Tile label="Publicações" value={num(data?.posts.length ?? 0)} />
        <Tile label="Engajamento" value={num(eng)} />
        <Tile label="Impressões" value={num(impr)} />
        <Tile label="Alcance" value={num(data?.resumo?.usuarios_alcancados ?? null)} />
        <Tile label="Seguidores" value={num(data?.resumo?.seguidores_total ?? null)} />
        <Tile label="Novos seg." value={num(data?.resumo?.novos_seguidores ?? null)} />
        <Tile label="Taxa engaj." value={taxa === null ? "—" : `${(taxa * 100).toFixed(2)}%`} hint="eng ÷ impr" />
      </div>

      {insights.length > 0 && (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {insights.map((n, i) => (
            <Insight key={i} tone={i % 2 === 0 ? "mint" : "plum"}>{n}</Insight>
          ))}
        </div>
      )}

      {top.length > 0 && (
        <div className="rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.04)" }}>
          <p className="mb-3 text-sm font-semibold text-white">Top publicações por engajamento</p>
          <BarList items={top.map((p) => ({ label: ddmm(p.data), value: p.engajamentos, valueLabel: num(p.engajamentos) }))} />
        </div>
      )}

      {uploadBox}

      {data && data.posts.length === 0 && (
        <p className="text-sm" style={{ color: C.lilacSoft }}>Nenhuma publicação. Suba o export para ver os dados.</p>
      )}
    </div>
  );
}

// ---- Conteúdo (um sócio) ----
function ConteudoView({
  data,
  fonte,
  setFonte,
  uploadBox,
}: {
  data: LinkedInResponse | null;
  fonte: "conteudo" | "publico";
  setFonte: (f: "conteudo" | "publico") => void;
  uploadBox: React.ReactNode;
}) {
  const demo = data?.demografia?.[fonte];
  const has = demo && Object.keys(demo).length > 0;
  const insights = conteudoInsights(demo);
  const blocks: [string, string][] = [
    ["Setor", "Setores"],
    ["Nível de experiência", "Senioridade"],
    ["Cargo", "Cargos"],
    ["Tamanho da empresa", "Porte da empresa"],
    ["Localidade", "Localização"],
    ["Empresa", "Empresas"],
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <span className="text-xs" style={{ color: C.lilacSoft }}>Base:</span>
        {(["conteudo", "publico"] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFonte(f)}
            className="rounded-full px-3 py-1 text-xs font-medium"
            style={fonte === f ? { background: C.accent, color: "#0E2A20" } : { background: "rgba(255,255,255,0.08)", color: C.lilacSoft }}
          >
            {f === "conteudo" ? "Quem viu o conteúdo" : "Seguidores (público)"}
          </button>
        ))}
      </div>

      {insights.length > 0 && (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {insights.map((n, i) => (
            <Insight key={i} tone={(["mint", "plum", "cream"] as const)[i % 3]}>{n}</Insight>
          ))}
        </div>
      )}

      {has ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {blocks.map(([key, label], i) => {
            const items = cat(demo, key);
            if (!items.length) return null;
            const color = [C.accent, C.lilacSoft, C.cream][i % 3];
            return (
              <div key={key} className="rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.04)" }}>
                <p className="mb-3 text-sm font-semibold text-white">{label}</p>
                <BarList items={topItems(items)} color={color} />
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm" style={{ color: C.lilacSoft }}>
          Sem demografia ainda. Suba o export de conteúdo para ver segmento, perfil e localização.
        </p>
      )}

      {uploadBox}
    </div>
  );
}

// ---- Comparativos ----
function ComparativoPublicacao({ all }: { all: Record<string, LinkedInResponse> }) {
  const max = Math.max(1, ...SOCIOS.map((s) => totalEngaj(all[s.key])));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {SOCIOS.map((s) => {
          const d = all[s.key];
          const eng = totalEngaj(d);
          const impr = d?.resumo?.impressoes_total ?? 0;
          const taxa = impr > 0 ? (eng / impr) * 100 : null;
          return (
            <div key={s.key} className="rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.06)" }}>
              <p className="text-sm font-semibold text-white">{s.nome}</p>
              <p className="mt-1 text-3xl font-semibold" style={{ color: C.accent }}>{num(eng)}</p>
              <p className="text-xs" style={{ color: C.lilacSoft }}>
                {d?.posts.length ?? 0} posts · {num(d?.resumo?.seguidores_total ?? null)} seguidores · taxa {taxa === null ? "—" : `${taxa.toFixed(2)}%`}
              </p>
            </div>
          );
        })}
      </div>
      <div className="rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.04)" }}>
        <p className="mb-3 text-sm font-semibold text-white">Engajamento total por sócio</p>
        <BarList items={SOCIOS.map((s) => ({ label: s.nome.split(" ")[0], value: totalEngaj(all[s.key]), valueLabel: num(totalEngaj(all[s.key])) }))} max={max} />
      </div>
    </div>
  );
}

function ComparativoConteudo({ all }: { all: Record<string, LinkedInResponse> }) {
  const agg: Record<string, number> = {};
  SOCIOS.forEach((s) => {
    cat(all[s.key]?.demografia?.conteudo, "Setor").forEach((it) => {
      if (it.pct != null) agg[it.valor] = (agg[it.valor] ?? 0) + it.pct;
    });
  });
  const ranked = Object.entries(agg)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([valor, v]) => ({ label: valor, value: v, valueLabel: pct(v / SOCIOS.length) }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {SOCIOS.map((s) => {
          const setor = cat(all[s.key]?.demografia?.conteudo, "Setor")[0];
          const nivel = cat(all[s.key]?.demografia?.conteudo, "Nível de experiência")[0];
          const loc = cat(all[s.key]?.demografia?.conteudo, "Localidade")[0];
          return (
            <div key={s.key} className="rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.06)" }}>
              <p className="text-sm font-semibold text-white">{s.nome}</p>
              <p className="mt-2 text-xs" style={{ color: C.lilacSoft }}>Setor: <span className="text-white">{setor?.valor ?? "—"}</span></p>
              <p className="text-xs" style={{ color: C.lilacSoft }}>Senioridade: <span className="text-white">{nivel?.valor ?? "—"}</span></p>
              <p className="text-xs" style={{ color: C.lilacSoft }}>Local: <span className="text-white">{loc?.valor ?? "—"}</span></p>
            </div>
          );
        })}
      </div>
      {ranked.length > 0 && (
        <div className="rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.04)" }}>
          <p className="mb-1 text-sm font-semibold text-white">Setores mais alcançados (consolidado)</p>
          <p className="mb-3 text-[11px]" style={{ color: "rgba(215,201,215,0.7)" }}>Média dos percentuais entre os sócios.</p>
          <BarList items={ranked} color={C.accent} max={Math.max(...Object.values(agg))} />
        </div>
      )}
    </div>
  );
}

// ---- Histórico de cargas + mapa semanal ----
function HistoryView({ socio }: { socio: string }) {
  const [hist, setHist] = useState<LinkedInHistory | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get<LinkedInHistory>(`/linkedin/history/?socio=${socio}`)
      .then((r) => setHist(r.data))
      .finally(() => setLoading(false));
  }, [socio]);

  if (loading) return <p className="text-sm" style={{ color: C.lilacSoft }}>Carregando...</p>;
  if (!hist) return null;

  const gaps = hist.semanas.filter((w) => !w.coberta && !w.atual).length;

  const kindLabel = (k: string) => (k === "conteudo" ? "Conteúdo" : k === "publicacao" ? "Publicação" : k);
  const fmt = (iso: string | null) => {
    if (!iso) return "—";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y.slice(2)}`;
  };

  return (
    <div className="space-y-5">
      {/* Mapa semanal */}
      <div className="rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.04)" }}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-semibold text-white">Cobertura semanal (13 semanas)</p>
          <p className="text-[11px]" style={{ color: "rgba(215,201,215,0.7)" }}>
            Atualização toda segunda, últimos 7 dias.
          </p>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {hist.semanas.map((w) => (
            <div
              key={w.inicio}
              title={`${fmt(w.inicio)} – ${fmt(w.fim)}${w.coberta ? " · atualizada" : w.atual ? " · semana atual" : " · sem atualização"}`}
              className="flex h-12 w-12 flex-col items-center justify-center rounded-lg text-[9px] font-semibold"
              style={{
                background: w.coberta ? C.accent : w.atual ? "rgba(240,217,168,0.25)" : "rgba(255,255,255,0.06)",
                color: w.coberta ? "#0E2A20" : w.atual ? C.cream : "rgba(215,201,215,0.6)",
                border: w.atual ? `1px solid ${C.cream}` : "1px solid transparent",
              }}
            >
              <span>{fmt(w.inicio).slice(0, 5)}</span>
              <span className="mt-0.5 text-[8px]">{w.coberta ? "ok" : w.atual ? "atual" : "—"}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-[11px]" style={{ color: C.lilacSoft }}>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: C.accent }} /> atualizada</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: "rgba(255,255,255,0.15)" }} /> sem atualização</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: C.cream }} /> semana atual</span>
        </div>
        {gaps > 0 && (
          <div className="mt-3">
            <Insight tone="cream">
              <strong>{gaps}</strong> {gaps === 1 ? "semana" : "semanas"} sem atualização no período. Suba a planilha dos últimos 7 dias para fechar a lacuna.
            </Insight>
          </div>
        )}
      </div>

      {/* Log de cargas */}
      <div className="rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.04)" }}>
        <p className="mb-3 text-sm font-semibold text-white">Cargas enviadas</p>
        {hist.logs.length === 0 ? (
          <p className="text-sm" style={{ color: C.lilacSoft }}>Nenhuma carga registrada ainda.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr style={{ color: "rgba(215,201,215,0.7)" }} className="text-left text-[11px] uppercase tracking-wide">
                  <th className="py-1 pr-4">Enviado em</th>
                  <th className="py-1 pr-4">Tipo</th>
                  <th className="py-1 pr-4">Período coberto</th>
                  <th className="py-1 pr-4">Registros</th>
                  <th className="py-1">Arquivo</th>
                </tr>
              </thead>
              <tbody style={{ color: "#F4EFF4" }}>
                {hist.logs.map((l) => (
                  <tr key={l.id} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                    <td className="py-2 pr-4">{fmt(l.created_at.slice(0, 10))}</td>
                    <td className="py-2 pr-4">{kindLabel(l.kind)}</td>
                    <td className="py-2 pr-4">{fmt(l.periodo_inicio)} – {fmt(l.periodo_fim)}</td>
                    <td className="py-2 pr-4">{l.kind === "conteudo" ? `${l.demografia_count} demo` : `${l.posts_count} posts`}</td>
                    <td className="py-2 truncate" style={{ maxWidth: 220, color: C.lilacSoft }}>{l.filename}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
